import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Grid,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import {
    DowngradeConfirmation,
    UpgradeConfirmation,
    patchAccountFromResponse,
    planMonthlyPrice,
    SCROLL_DURATION_MS,
    smoothScrollToElement,
    SubscriptionPlanCard,
} from '../../components/subscription/subscriptionPlanMigration';
import { readPaywallFeatureKey, readPaywallReturnTo } from '../../helpers/paywall';
import {
    canonicalPlanName,
    comparePlanChange,
    formatPlanRenewalDate,
    isSameSubscriptionPlan,
    planTierRank,
} from '../../helpers/subscription';

function PaywallPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnTo = readPaywallReturnTo(searchParams);
    const featureKey = readPaywallFeatureKey(searchParams);
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedChangeType, setSelectedChangeType] = useState(null);
    const termsSectionRef = useRef(null);
    const scrollAnimationRef = useRef(null);

    const currentPlan = subscription?.plan;
    const pendingPlan = subscription?.pending_plan;
    const pendingDowngradePlan = subscription?.pending_downgrade_plan;
    const planMigrationPending = Boolean(subscription?.plan_migration_pending);
    const planDowngradePending = Boolean(subscription?.plan_downgrade_pending);
    const plans = Array.isArray(subscription?.plans) ? subscription.plans : [];
    const currentPlanName = currentPlan?.name || 'Free';
    const renewalDateLabel = formatPlanRenewalDate(subscription?.plan_renewal_date);

    const sortedPlans = useMemo(
        () => [...plans].sort(
            (a, b) => planTierRank(a.name, planMonthlyPrice(a)) - planTierRank(b.name, planMonthlyPrice(b)),
        ),
        [plans],
    );

    const recommendedUpgradePlan = useMemo(() => {
        if (!featureKey || !currentPlan?.name) {
            return null;
        }
        const upgrades = sortedPlans.filter(
            (plan) => comparePlanChange(
                currentPlan.name,
                planMonthlyPrice(currentPlan),
                plan.name,
                planMonthlyPrice(plan),
            ) === 'upgrade',
        );
        if (upgrades.length === 0) {
            return null;
        }
        return upgrades.sort(
            (a, b) => planTierRank(b.name, planMonthlyPrice(b)) - planTierRank(a.name, planMonthlyPrice(a)),
        )[0];
    }, [featureKey, currentPlan, sortedPlans]);

    const isMigratingPlan = useCallback((plan) => {
        if (!planMigrationPending || !pendingPlan) {
            return false;
        }
        if (pendingPlan.uuid && plan.uuid) {
            return pendingPlan.uuid === plan.uuid;
        }
        return isSameSubscriptionPlan(pendingPlan.name, plan.name);
    }, [planMigrationPending, pendingPlan]);

    const isPendingDowngradePlan = useCallback((plan) => {
        if (!planDowngradePending || !pendingDowngradePlan) {
            return false;
        }
        if (pendingDowngradePlan.uuid && plan.uuid) {
            return pendingDowngradePlan.uuid === plan.uuid;
        }
        return isSameSubscriptionPlan(pendingDowngradePlan.name, plan.name);
    }, [planDowngradePending, pendingDowngradePlan]);

    const loadSubscription = useCallback(() => (
        api.get('/me/subscription')
            .then((response) => {
                const data = response?.data?.data || null;
                setSubscription(data);
                if (data?.plan?.name) {
                    patchAccountFromResponse(data);
                }
                return data;
            })
            .catch(() => null)
    ), []);

    const isPlanSelected = (plan) => {
        if (!selectedPlan) {
            return false;
        }
        if (selectedPlan.uuid && plan.uuid) {
            return selectedPlan.uuid === plan.uuid;
        }
        return selectedPlan.name === plan.name;
    };

    const handleSelectPlan = (plan) => {
        const direction = comparePlanChange(
            currentPlan?.name,
            planMonthlyPrice(currentPlan),
            plan.name,
            planMonthlyPrice(plan),
        );
        flushSync(() => {
            setSelectedPlan(plan);
            setSelectedChangeType(direction);
        });
        smoothScrollToElement(termsSectionRef.current, SCROLL_DURATION_MS, scrollAnimationRef);
    };

    useEffect(() => () => {
        if (scrollAnimationRef.current) {
            cancelAnimationFrame(scrollAnimationRef.current);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        loadSubscription()
            .then(() => {
                if (!cancelled) {
                    setError('');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError('Não foi possível carregar os planos disponíveis.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [loadSubscription]);

    const intro = useMemo(() => {
        if (featureKey) {
            return `A funcionalidade que você está querendo acessar não está incluída no plano ${currentPlanName}.\n\nSeu plano atual continua destacado abaixo. Você pode migrar para um plano superior ou regredir para um plano inferior.`;
        }
        return `Seu plano atual é ${currentPlanName}. Compare os planos abaixo para migrar ou regredir.`;
    }, [featureKey, currentPlanName]);

    const handleUpgradeSuccess = () => {
        navigate('/admin/my-account/subscription');
    };

    const handleDowngradeSuccess = async (data) => {
        setSelectedPlan(null);
        setSelectedChangeType(null);
        if (data) {
            setSubscription((prev) => ({ ...(prev || {}), ...data }));
            patchAccountFromResponse(data);
        } else {
            await loadSubscription();
        }
    };

    const resetSelection = () => {
        setSelectedPlan(null);
        setSelectedChangeType(null);
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Typography color="text.primary">Planos</Typography>
            </Breadcrumbs>

            <Typography variant="h4" sx={{ mb: 1 }}>
                Planos Acompanha 3D
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 720, whiteSpace: 'pre-line' }}>
                {intro}
            </Typography>

            {!loading && subscription ? (
                <Alert severity="info" sx={{ mb: 3, maxWidth: 720 }}>
                    Plano ativo: <strong>{canonicalPlanName(currentPlanName)}</strong>
                    {renewalDateLabel ? (
                        <>
                            {' '}· Próxima cobrança em <strong>{renewalDateLabel}</strong>
                        </>
                    ) : null}
                    {planMigrationPending && pendingPlan?.name ? (
                        <>
                            {' '}· Migração pendente para <strong>{pendingPlan.name}</strong>
                        </>
                    ) : null}
                    {planDowngradePending && pendingDowngradePlan?.name ? (
                        <>
                            {' '}· Regressão agendada para <strong>{pendingDowngradePlan.name}</strong>
                            {renewalDateLabel ? (
                                <>
                                    {' '}em <strong>{renewalDateLabel}</strong>
                                </>
                            ) : null}
                        </>
                    ) : null}
                </Alert>
            ) : null}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : null}

            {!loading && error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            {!loading && !error ? (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {sortedPlans.map((plan) => {
                        const changeDirection = comparePlanChange(
                            currentPlan?.name,
                            planMonthlyPrice(currentPlan),
                            plan.name,
                            planMonthlyPrice(plan),
                        );
                        const isRecommendedUpgrade = Boolean(
                            recommendedUpgradePlan
                            && (recommendedUpgradePlan.uuid
                                ? recommendedUpgradePlan.uuid === plan.uuid
                                : isSameSubscriptionPlan(recommendedUpgradePlan.name, plan.name)),
                        );
                        return (
                            <Grid item xs={12} sm={6} md={sortedPlans.length >= 4 ? 3 : 4} key={plan.uuid || plan.name}>
                                <SubscriptionPlanCard
                                    plan={plan}
                                    isCurrent={isSameSubscriptionPlan(currentPlan?.name, plan.name)}
                                    isSelected={isPlanSelected(plan)}
                                    isMigrating={isMigratingPlan(plan)}
                                    isPendingDowngrade={isPendingDowngradePlan(plan)}
                                    isRecommendedUpgrade={isRecommendedUpgrade}
                                    changeDirection={changeDirection}
                                    onSelect={handleSelectPlan}
                                    busy={false}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            ) : null}

            <Box ref={termsSectionRef} sx={{ scrollMarginTop: 24 }}>
                {!loading && !error && selectedPlan && selectedChangeType === 'upgrade' ? (
                    <UpgradeConfirmation
                        key={`upgrade-${selectedPlan.uuid || selectedPlan.name}`}
                        plan={selectedPlan}
                        onBack={resetSelection}
                        onSuccess={handleUpgradeSuccess}
                    />
                ) : null}
                {!loading && !error && selectedPlan && selectedChangeType === 'downgrade' ? (
                    <DowngradeConfirmation
                        key={`downgrade-${selectedPlan.uuid || selectedPlan.name}`}
                        plan={selectedPlan}
                        currentPlanName={canonicalPlanName(currentPlanName)}
                        renewalDateLabel={renewalDateLabel}
                        onBack={resetSelection}
                        onSuccess={handleDowngradeSuccess}
                    />
                ) : null}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={() => navigate(returnTo)}>
                    Voltar
                </Button>
            </Box>
        </Box>
    );
}

export default PaywallPage;
